/**
 * liteDisplayUtils.js
 *
 * Pure calculation helpers used by LitePropertiesPanel.
 * Extracted so the component stays thin and testable.
 */

const DEFAULT_FPS = 25;

/**
 * Known field-type keys that map to editable categories.
 */
const FIELD_TYPE_KEYS = ['textField', 'imageField', 'videoField', 'audioField', 'colorField'];

/**
 * Collect every editable display-item that is visible at `displayTime`.
 *
 * Returns an array of items, each shaped as:
 *   { kind, elementId, elementName, fieldIndex?, fieldType?, value, category, layerName? }
 *
 * @param {Array}  canvasElements – full list of canvas elements
 * @param {number} displayTime    – current time in seconds
 * @param {number} [fps]          – frames per second (default 25)
 */
export const collectDisplayItems = (canvasElements, displayTime, fps = DEFAULT_FPS) => {
  if (!canvasElements || canvasElements.length === 0) return [];

  const currentFrame = Math.floor(displayTime * fps);
  const items = [];

  canvasElements.forEach((el) => {
    // Skip elements outside their timeline range
    const timeline = el.timeline;
    if (timeline) {
      if (currentFrame < timeline.inPoint || currentFrame > timeline.outPoint) return;
    }

    const elType = (el.type || '').toLowerCase();
    const hasFields =
      Array.isArray(el.componentData?.fields) && el.componentData.fields.length > 0;

    // ── agComponent: extract individual text / image / video fields ──
    // Simple mode shows text, image, video, and audio fields (not color).
    const SIMPLE_FIELD_KEYS = ['textField', 'imageField', 'videoField', 'audioField'];

    if (hasFields) {
      el.componentData.fields.forEach((field, fieldIndex) => {
        if (!field) return;

        const fieldType = Object.keys(field).find((key) => SIMPLE_FIELD_KEYS.includes(key));
        if (!fieldType) return;

        const value = field[fieldType];

        let category = 'other';
        if (fieldType.includes('text')) category = 'text';
        else if (fieldType.includes('image')) category = 'image';
        else if (fieldType.includes('video')) category = 'video';
        else if (fieldType.includes('audio')) category = 'audio';

        if (!['text', 'image', 'video', 'audio'].includes(category)) return;

        items.push({
          kind: 'field',
          elementId: el.id,
          elementName: el.name || el.componentData?.title || 'Component',
          fieldIndex,
          fieldType,
          value,
          category,
          layerName: field.layerName || '',
          // Carry target dimensions & duration so the crop/trim modal
          // knows the constraints for this asset slot
          targetWidth: field.width || null,
          targetHeight: field.height || null,
          duration: field.duration || null,
          // Carry poster/thumbnail for video preview display
          poster: category === 'video'
            ? (field.poster || field.thumbnail || field.preview || null)
            : null,
        });
      });
      return; // don't also treat it as a standalone element
    }

    // ── Standalone text element ────────────────────────────────────────
    if (elType === 'text') {
      const textValue = el.componentData?.text || el.pixiSprite?.text || 'Text';
      items.push({
        kind: 'element',
        elementId: el.id,
        elementName: el.name || el.componentData?.title || 'Text',
        value: textValue,
        category: 'text',
        originalType: 'text',
      });
      return;
    }

    // ── AI-generated element ───────────────────────────────────────────
    if (elType === 'ai') {
      const aiMediaType = el.componentData?.aiMediaType || 'image';
      const isPlayable = aiMediaType === 'video' || aiMediaType === 'audio';
      const url = isPlayable
        ? (el.componentData?.url || el.src || el.url ||
           el.componentData?.preview || el.componentData?.thumbnail || null)
        : (el.componentData?.preview || el.componentData?.thumbnail ||
           el.componentData?.url || el.src || el.url || null);

      const category = aiMediaType === 'gif' ? 'image' : aiMediaType;

      items.push({
        kind: 'element',
        elementId: el.id,
        elementName: el.name || el.componentData?.title || el.componentData?.name || 'AI Media',
        value: url,
        category,
        originalType: 'ai',
        isAiGenerated: true,
        poster: isPlayable
          ? (el.componentData?.thumbnail || el.componentData?.preview || null)
          : null,
      });
      return;
    }

    // ── Standalone media element ─────────────────────────────────────
    if (['image', 'video', 'audio', 'gif'].includes(elType)) {
      // For video/audio elements, prefer the actual media URL over
      // preview/thumbnail which are typically image poster URLs
      // (e.g. Pexels videos store an image URL in `thumbnail`).
      const isPlayable = elType === 'video' || elType === 'audio';
      const url = isPlayable
        ? (el.componentData?.url || el.src || el.url ||
           el.componentData?.preview || el.componentData?.thumbnail || null)
        : (el.componentData?.preview || el.componentData?.thumbnail ||
           el.componentData?.url || el.src || el.url || null);

      // category drives thumbnail rendering (gif renders like an image)
      const category = elType === 'gif' ? 'image' : elType;

      items.push({
        kind: 'element',
        elementId: el.id,
        elementName:
          el.name || el.componentData?.title || el.componentData?.name || 'Media',
        value: url,
        category,
        // Keep the real element type so callers can open the correct
        // media-selector tab (e.g. 'gif' → GIFs tab, not Images).
        originalType: elType,
        // Carry poster/thumbnail for video preview display
        poster: isPlayable
          ? (el.componentData?.thumbnail || el.componentData?.preview || null)
          : null,
      });
    }

    // ── Captions (can be on any element, usually media) ──────────────
    // N.B: We no longer include captions in the simple `collectDisplayItems` array
    // so they are intentionally hidden from the simple carousel view.
    // They will still be collected by `collectLayerProperties` for "Show All Edits".
  });

  return items;
};

/**
 * Build the rotated list of secondary (non-focused) items.
 *
 * Starting from focusedIndex+1, wraps around, returns up to `maxCount` items.
 * Each item gets an `_originalIdx` property so the UI can map clicks back.
 *
 * @param {Array}  displayItems
 * @param {number} focusedIndex
 * @param {number} [maxCount=3]
 */
export const getRotatedSecondaryItems = (displayItems, focusedIndex, maxCount = 3) => {
  const total = displayItems.length;
  if (total <= 1) return [];

  const rotated = [];
  for (let i = 1; i < total; i++) {
    const idx = (focusedIndex + i) % total;
    rotated.push({ ...displayItems[idx], _originalIdx: idx });
  }
  return rotated.slice(0, maxCount);
};

/**
 * Compute the vertical progress-bar metrics.
 *
 * @param {number} total        – total number of items
 * @param {number} focusedIndex – currently focused index
 * @returns {{ thumbPct: number, topPct: number }}
 */
export const getProgressMetrics = (total, focusedIndex) => {
  const thumbPct = total > 0 ? (1 / total) * 100 : 100;
  const topPct =
    total > 1 ? (focusedIndex / (total - 1)) * (100 - thumbPct) : 0;
  return { thumbPct, topPct };
};

/**
 * Collect ALL properties for each visible layer/element at `displayTime`,
 * grouped into "fields" (text, image, video, audio, color) and "controls"
 * (opacity, volume, mute, playback mode).
 *
 * Returns an array of layer objects, each shaped as:
 * {
 *   elementId, elementName, element (ref),
 *   fields: [ { fieldIndex, fieldType, value, category, layerName, targetWidth, targetHeight, duration } ],
 *   controls: { opacity, hasMediaControls, volume, muted, playbackMode }
 * }
 *
 * @param {Array}  canvasElements – full list of canvas elements
 * @param {number} displayTime    – current time in seconds
 * @param {number} [fps]          – frames per second (default 25)
 */
export const collectLayerProperties = (canvasElements, displayTime, fps = DEFAULT_FPS) => {
  if (!canvasElements || canvasElements.length === 0) return [];

  const currentFrame = Math.floor(displayTime * fps);
  const layers = [];

  canvasElements.forEach((el) => {
    // Skip elements outside their timeline range
    const timeline = el.timeline;
    if (timeline) {
      if (currentFrame < timeline.inPoint || currentFrame > timeline.outPoint) return;
    }

    const elType = (el.type || '').toLowerCase();
    const hasFields =
      Array.isArray(el.componentData?.fields) && el.componentData.fields.length > 0;
    const isMedia = ['image', 'video', 'audio', 'gif'].includes(elType);
    const isText = elType === 'text';
    const isAi = elType === 'ai';

    // Skip elements that have no editable content at all
    if (!hasFields && !isMedia && !isText && !isAi) return;

    const layer = {
      elementId: el.id,
      elementName: el.name || el.componentData?.title || el.componentData?.name || 'Layer',
      element: el, // keep ref for property changes
      fields: [],
      controls: {
        opacity: el.alpha !== undefined ? el.alpha : 1,
        hasMediaControls: !!(el.pixiSprite?.videoElement || el.pixiSprite?.soundId),
        volume: el.volume !== undefined ? el.volume : 1,
        muted: !!el.muted,
        playbackMode: el.playbackMode || 'loop',
      },
    };

    // ── agComponent: extract individual fields ──
    if (hasFields) {
      el.componentData.fields.forEach((field, fieldIndex) => {
        if (!field) return;

        const fieldType = Object.keys(field).find((key) => FIELD_TYPE_KEYS.includes(key));
        if (!fieldType) return;

        const value = field[fieldType];

        let category = 'other';
        if (fieldType.includes('text')) category = 'text';
        else if (fieldType.includes('color')) category = 'color';
        else if (fieldType.includes('image')) category = 'image';
        else if (fieldType.includes('video')) category = 'video';
        else if (fieldType.includes('audio')) category = 'audio';

        layer.fields.push({
          kind: 'field',
          fieldIndex,
          fieldType,
          value,
          category,
          layerName: field.layerName || '',
          targetWidth: field.width || null,
          targetHeight: field.height || null,
          duration: field.duration || null,
          // Font family stored alongside the text field
          fontFamily: field[`${fieldType}_fontFamily`] || null,
          // Carry poster/thumbnail for video preview display
          poster: category === 'video'
            ? (field.poster || field.thumbnail || field.preview || null)
            : null,
        });
      });
    } else if (isText) {
      // ── Standalone text element ──
      const textValue = el.componentData?.text || el.pixiSprite?.text || 'Text';
      layer.fields.push({
        kind: 'element',
        fieldIndex: null,
        fieldType: null,
        value: textValue,
        category: 'text',
        layerName: '',
        originalType: 'text',
        targetWidth: null,
        targetHeight: null,
        duration: null,
      });
    } else if (isAi) {
      // ── AI-generated element — expose media, prompt, and reference for editing ──
      const aiMediaType = el.componentData?.aiMediaType || 'image';
      const isPlayable = aiMediaType === 'video' || aiMediaType === 'audio';
      const url = isPlayable
        ? (el.componentData?.url || el.src || el.url ||
           el.componentData?.preview || el.componentData?.thumbnail || null)
        : (el.componentData?.preview || el.componentData?.thumbnail ||
           el.componentData?.url || el.src || el.url || null);

      const category = aiMediaType === 'gif' ? 'image' : aiMediaType;

      layer.fields.push({
        kind: 'element',
        fieldIndex: null,
        fieldType: null,
        value: url,
        category,
        layerName: '',
        originalType: 'ai',
        isAiGenerated: true,
        targetWidth: null,
        targetHeight: null,
        duration: null,
        poster: isPlayable
          ? (el.componentData?.thumbnail || el.componentData?.preview || null)
          : null,
      });

      // Mark this layer as AI so the UI can render the AI-specific editing panel
      layer.isAiLayer = true;
      layer.aiPayload = el.componentData?.aiPayload || null;
    } else if (isMedia) {
      // ── Standalone media element — treat as a single "field" ──
      // For video/audio elements, prefer the actual media URL over
      // preview/thumbnail which are typically image poster URLs.
      const isPlayable = elType === 'video' || elType === 'audio';
      const url = isPlayable
        ? (el.componentData?.url || el.src || el.url ||
           el.componentData?.preview || el.componentData?.thumbnail || null)
        : (el.componentData?.preview || el.componentData?.thumbnail ||
           el.componentData?.url || el.src || el.url || null);

      const category = elType === 'gif' ? 'image' : elType;

      layer.fields.push({
        kind: 'element',
        fieldIndex: null,
        fieldType: null,
        value: url,
        category,
        layerName: '',
        originalType: elType,
        targetWidth: null,
        targetHeight: null,
        duration: null,
        // Carry poster/thumbnail for video preview display
        poster: isPlayable
          ? (el.componentData?.thumbnail || el.componentData?.preview || null)
          : null,
      });
    }

    // ── Captions (can be on any element, usually media) ──────────────
    const hasCaptions = el.componentData?.captions?.phrases && el.componentData.captions.phrases.length > 0;
    const isPlayableElement = elType === 'video' || elType === 'audio';
    if (hasCaptions || isPlayableElement) {
      const fullText = hasCaptions ? el.componentData.captions.phrases.map(p => p.text).join(' ') : 'Generate Captions';
      layer.fields.push({
        kind: 'element',
        fieldIndex: null,
        fieldType: null,
        value: fullText,
        category: 'text',
        layerName: 'Captions',
        originalType: 'caption',
        targetWidth: null,
        targetHeight: null,
        duration: null,
      });
    }

    layers.push(layer);
  });

  return layers;
};

/**
 * Derive the two-line question text from the focused item's category.
 *
 * @param {object|null} primaryItem
 * @returns {{ line1: string, line2: string }}
 */
export const getQuestionText = (primaryItem) => {
  if (!primaryItem) return { line1: 'No editable', line2: 'fields here' };

  if (primaryItem.isAiGenerated) {
    return { line1: 'This is AI generated.', line2: 'Tap to edit or regenerate.' };
  }

  switch (primaryItem.category) {
    case 'image':
      return { line1: 'Would you like to', line2: 'change the image?' };
    case 'video':
      return { line1: 'Would you like to', line2: 'change the video?' };
    case 'audio':
      return { line1: 'Would you like to', line2: 'change the audio?' };
    case 'text':
      return { line1: 'Would you like to', line2: 'edit the text?' };
    default:
      return { line1: 'Would you like to', line2: 'make a change?' };
  }
};
